<template>
    <BaseDialog v-model:open="open" :title="goal.title" :show-close="!isSavingTargetDate && !isDeleting" :disable-close-on-backdrop-click="isSavingTargetDate || isDeleting">
        <div ref="dialogElement" data-testid="development-goal-details-dialog" class="flex flex-col gap-4">
            <p class="flex items-center gap-2">
                <span class="size-2 shrink-0 rounded-full bg-journey-marker" aria-hidden="true"></span>
                <span class="scaffold-details-small text-ink-muted">Started {{ formatStartedOn(goal.created_at) }}</span>
            </p>

            <div class="flex flex-col gap-2.5">
                <section class="flex flex-col">
                    <h3 class="scaffold-body-small-medium text-ink">What success looks like</h3>
                    <p class="details-big text-ink-muted">{{ goal.achievement_criteria }}</p>
                </section>

                <section class="flex flex-col self-start">
                    <BaseNotification v-if="targetDateError" class="mb-1" theme="error" :text="targetDateError" hide-dismiss />
                    <h3 v-if="goal.target_date" class="scaffold-body-small-medium text-ink">Target date</h3>
                    <div class="details-big flex items-center gap-2 text-ink-muted">
                        <time v-if="goal.target_date" :datetime="goal.target_date">{{ formatTargetDate(goal.target_date) }}</time>
                        <BasePopover v-model:open="isCalendarOpen" as-child side="bottom" align="start" :side-offset="6" content-class="p-0">
                            <template #trigger>
                                <JourneyButton
                                    v-if="goal.target_date"
                                    type="button"
                                    theme="tertiary"
                                    size="sm"
                                    icon-only
                                    :aria-label="`Change target date (currently ${formatTargetDate(goal.target_date)})`"
                                    :disabled="isSavingTargetDate"
                                    data-testid="edit-development-goal-target-date">
                                    <Pencil />
                                </JourneyButton>
                                <JourneyButton
                                    v-else
                                    type="button"
                                    theme="neutral"
                                    size="sm"
                                    class="inline-flex w-fit items-center gap-2.5 px-4 py-2.5"
                                    aria-label="Set target date"
                                    :disabled="isSavingTargetDate"
                                    data-testid="edit-development-goal-target-date">
                                    <Calendar class="size-4 shrink-0" foreground-class="stroke-current" />
                                    <span>Set target date</span>
                                </JourneyButton>
                            </template>
                            <template #content>
                                <BaseCalendar
                                    :model-value="selectedTargetDate"
                                    :min-value="minimumTargetDate"
                                    :disabled="isSavingTargetDate"
                                    surface="plain"
                                    @update:model-value="handleTargetDateSelected" />
                            </template>
                        </BasePopover>
                    </div>
                </section>
            </div>

            <HorizontalDivider as="hr" spacing="none" />

            <div>
                <BaseNotification v-if="deleteError" class="mb-2.5" theme="error" :text="deleteError" hide-dismiss />
                <p data-testid="delete-development-goal-announcement" class="sr-only" aria-live="polite" aria-atomic="true">
                    {{ isConfirmingDeletion ? "Delete this goal? This can't be undone. Confirm deletion to continue." : "" }}
                </p>
                <div
                    v-if="isConfirmingDeletion"
                    class="flex flex-col items-start gap-2.5 rounded-sm bg-surface-sunken px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-2.5 sm:py-1"
                    data-testid="delete-development-goal-warning">
                    <p class="scaffold-details-small text-ink-muted sm:whitespace-nowrap">
                        <span class="text-ink">Delete this goal?</span>
                        This can't be undone
                    </p>
                    <div class="flex w-full shrink-0 items-center gap-2.5 sm:w-auto sm:justify-end">
                        <JourneyButton
                            type="button"
                            theme="neutral"
                            size="sm"
                            shape="square"
                            class="min-h-11 flex-1 sm:min-h-0 sm:flex-none"
                            :disabled="isDeleting"
                            data-testid="cancel-delete-development-goal"
                            @click="void cancelDeletion()">
                            Cancel
                        </JourneyButton>
                        <JourneyButton
                            type="button"
                            theme="danger"
                            size="sm"
                            shape="square"
                            class="min-h-11 flex-1 sm:min-h-0 sm:flex-none"
                            :disabled="isDeleting"
                            data-testid="confirm-delete-development-goal"
                            @click="deleteGoal">
                            {{ isDeleting ? "Deleting…" : "Delete" }}
                        </JourneyButton>
                    </div>
                </div>
                <JourneyButton
                    v-else
                    theme="danger-text"
                    size="sm"
                    class="w-full py-3 pl-1 text-left"
                    data-testid="delete-development-goal"
                    @click="void startDeletionConfirmation()">
                    Delete goal
                </JourneyButton>
            </div>
        </div>
    </BaseDialog>
</template>

<script setup lang="ts">
import { type DateValue, parseDate, today } from "@internationalized/date";
import { fetchApi } from "~vue/api";
import type { DevelopmentGoal, UpdateDevelopmentGoalRequest } from "~vue/api/generated/development-journeys";
import BaseCalendar from "~vue/components/BaseCalendar.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePopover from "~vue/components/BasePopover.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import Calendar from "~vue/icons/Calendar.vue";
import Pencil from "~vue/icons/Pencil.vue";
import JourneyButton from "~vue/pages/developmentJourneys/DevelopmentJourneyButton.vue";
import { formatDate } from "~vue/utils/formatDate";
import { DateTime } from "luxon";
import { computed, nextTick, ref, watch } from "vue";

const { goal, timezone } = defineProps<{
    goal: DevelopmentGoal;
    timezone?: string;
}>();

const open = defineModel<boolean>("open", { default: false });
const emit = defineEmits<{ deleted: [goalId: string]; updated: [goal: DevelopmentGoal] }>();
const isConfirmingDeletion = ref(false);
const isDeleting = ref(false);
const deleteError = ref<string | null>(null);
const isSavingTargetDate = ref(false);
const targetDateError = ref<string | null>(null);
const isCalendarOpen = ref(false);
const dialogElement = ref<HTMLElement | null>(null);

const minimumTargetDate = computed(() => today(timezone ?? "UTC"));

const selectedTargetDate = computed(() => (goal.target_date ? parseDate(goal.target_date) : undefined));

watch(open, (isOpen) => {
    if (!isOpen) {
        isConfirmingDeletion.value = false;
        deleteError.value = null;
        targetDateError.value = null;
        isCalendarOpen.value = false;
    }
});

const handleTargetDateSelected = (value: DateValue | undefined) => {
    if (!value) {
        return;
    }

    const selected = value.toString();
    if (selected === goal.target_date) {
        return;
    }

    void saveTargetDate(selected);
};

const saveTargetDate = async (targetDate: string) => {
    if (isSavingTargetDate.value) {
        return;
    }

    isSavingTargetDate.value = true;
    targetDateError.value = null;
    try {
        const body = { target_date: targetDate } satisfies UpdateDevelopmentGoalRequest;
        const response = await fetchApi(`/v1/development-journeys/goals/${goal.id}`, { method: "PATCH", body: JSON.stringify(body) });
        if (!response.ok) {
            throw new Error("Unable to update the development goal target date");
        }
        const updatedGoal = (await response.json()) as DevelopmentGoal;
        emit("updated", updatedGoal);
        isCalendarOpen.value = false;
    } catch {
        targetDateError.value = "We couldn't update this target date. Please try again.";
    } finally {
        isSavingTargetDate.value = false;
    }
};

const focusDialogControl = async (testId: string) => {
    await nextTick();
    dialogElement.value?.querySelector<HTMLElement>(`[data-testid="${testId}"]`)?.focus();
};

const startDeletionConfirmation = async () => {
    isConfirmingDeletion.value = true;
    await focusDialogControl("cancel-delete-development-goal");
};

const cancelDeletion = async () => {
    isConfirmingDeletion.value = false;
    deleteError.value = null;
    await focusDialogControl("delete-development-goal");
};

const deleteGoal = async () => {
    if (isDeleting.value) {
        return;
    }

    isDeleting.value = true;
    deleteError.value = null;
    try {
        const response = await fetchApi(`/v1/development-journeys/goals/${goal.id}`, { method: "DELETE" });
        if (!response.ok && response.status !== 404) {
            throw new Error("Unable to delete development goal");
        }
        open.value = false;
        emit("deleted", goal.id);
    } catch {
        deleteError.value = "We couldn't delete this goal. Please try again.";
    } finally {
        isDeleting.value = false;
    }
};

const formatTargetDate = (targetDate: string) => formatDate(targetDate, "date-long");

const formatStartedOn = (createdAt: string) => formatDate(DateTime.fromISO(createdAt, { zone: timezone ?? "utc" }), "month-day-long") || createdAt;
</script>
